import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import type { Receipt, LeaderboardMetric } from "../../types";
import { formatDateShort } from "../../utils/formatters";

// =============================================================================
// RECEIPTS POPOVER
// =============================================================================
// PURPOSE: Portal-rendered popover showing per-cell receipt detail for leaderboard.
//
// REQUIREMENTS:
// - Portal rendering to document.body for z-index control
// - Auto-positions above/below based on viewport space
// - 200ms open delay, 250ms close delay (tooltip-on-tooltip — stays open while
//   cursor enters the popover surface)
// - Desktop: hover opens/closes (delayed). Touch: tap toggles directly, since
//   touch devices never fire hover — added 2026-07-14 responsive audit fix.
//   "Open full view →" button still triggers onOpenModal on both.
// - Click outside closes popover
// - CPC metric gets two sub-tables: cash side + show side
// - NO transform on hover states (css-glass rule — backdrop-filter blur)
// =============================================================================

// ---- Sub-components --------------------------------------------------------

interface ReceiptsTableProps {
  rows: Receipt[];
}

const ReceiptsTable: React.FC<ReceiptsTableProps> = ({ rows }) => (
  <table className="w-full text-left">
    <thead>
      <tr>
        <th className="token-11 text-glass-muted uppercase pb-1 pr-3 font-medium">Label</th>
        <th className="token-11 text-glass-muted uppercase pb-1 pr-3 font-medium">Value</th>
        <th className="token-11 text-glass-muted uppercase pb-1 font-medium text-right">Date</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r.id}>
          <td className="token-13 text-glass-secondary whitespace-nowrap pr-3 py-0.5 align-top">
            {r.label}
          </td>
          <td className="token-13 text-glass whitespace-nowrap pr-3 py-0.5 align-top">
            <span>{r.value}</span>
            {r.sublabel && <span className="block token-11 text-glass-muted">{r.sublabel}</span>}
          </td>
          <td className="token-13 text-glass-muted whitespace-nowrap py-0.5 text-right align-top">
            {formatDateShort(new Date(r.date + "T00:00:00"))}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

// ---- Popover portal --------------------------------------------------------

interface PopoverPanelProps {
  label: string;
  formula: string;
  computation: string;
  receipts: Receipt[] | undefined;
  metric: LeaderboardMetric;
  repId: string;
  anchorRect: DOMRect;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onOpenModal: () => void;
}

const PopoverPanel: React.FC<PopoverPanelProps> = ({
  label,
  formula,
  computation,
  receipts,
  metric,
  repId,
  anchorRect,
  onMouseEnter,
  onMouseLeave,
  onOpenModal,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0, opacity: 0 });

  const reposition = useCallback((rect: DOMRect) => {
    const el = panelRef.current;
    if (!el) return;
    const GAP = 8;
    const panelW = el.offsetWidth;
    const panelH = el.offsetHeight;

    let top = rect.bottom + GAP;
    let left = rect.left + rect.width / 2 - panelW / 2;

    // Flip above if overflows bottom
    if (top + panelH > window.innerHeight - 16) {
      top = rect.top - panelH - GAP;
    }
    // Clamp horizontal
    left = Math.max(8, Math.min(left, window.innerWidth - panelW - 8));
    // Clamp vertical
    top = Math.max(8, top);

    setPos({ top, left, opacity: 1 });
  }, []);

  useLayoutEffect(() => {
    reposition(anchorRect);
  }, [anchorRect, reposition]);

  useLayoutEffect(() => {
    const handler = () => reposition(anchorRect);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [anchorRect, reposition]);

  // Receipts header text
  let receiptsHeader: React.ReactNode;
  if (receipts === undefined) {
    receiptsHeader = <p className="token-14s text-glass-muted italic">Loading receipts…</p>;
  } else if (receipts.length === 0) {
    receiptsHeader = (
      <p className="token-14s text-glass-muted italic">No receipts in current range</p>
    );
  } else {
    receiptsHeader = (
      <p className="token-14s text-glass-muted">
        {receipts.length} entr{receipts.length === 1 ? "y" : "ies"}
      </p>
    );
  }

  // CPC: split into cash + show sub-tables
  const cashRows = receipts?.filter((r) => r.side === "cash") ?? [];
  const showRows = receipts?.filter((r) => r.side === "show") ?? [];
  const hasRows = receipts !== undefined && receipts.length > 0;

  return createPortal(
    <div
      ref={panelRef}
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        opacity: pos.opacity,
        zIndex: 9999,
      }}
      // Floating card — not a glass surface; bg-white matches HoverTooltip convention.
      // max-w caps the fixed w-96 (384px) on narrow phones — at 390px viewport the
      // panel otherwise clips its DATE column mid-glyph with no scroll recourse.
      className="w-96 max-w-[calc(100vw-16px)] p-4 rounded-lg bg-white border border-black/[0.12] shadow-xl transition-opacity duration-150"
      data-tooltip-key={`receipts-${repId}-${metric}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* 1. Header row */}
      <p className="token-11 text-glass-muted uppercase">{label}</p>

      {/* 2. Formula */}
      <p className="token-13 text-glass mt-1 font-mono">{formula}</p>

      {/* 3. Computation */}
      <p className="token-13 text-ds-accent mt-1 font-mono">{computation}</p>

      {/* 4. Divider */}
      <div className="border-t border-black/[0.08] my-2" />

      {/* 5. Receipts header */}
      {receiptsHeader}

      {/* 6. Receipts table — overflow-x lets whitespace-nowrap rows scroll when the
          panel is width-capped on narrow phones */}
      {hasRows && (
        <div className="max-h-72 overflow-y-auto overflow-x-auto mt-1">
          {metric === "cpc" ? (
            <>
              {cashRows.length > 0 && (
                <div className="mb-3">
                  <p className="token-11 text-glass-muted uppercase mb-1">
                    Cash receipts (numerator)
                  </p>
                  <ReceiptsTable rows={cashRows} />
                </div>
              )}
              {showRows.length > 0 && (
                <div>
                  <p className="token-11 text-glass-muted uppercase mb-1">
                    Show receipts (denominator)
                  </p>
                  <ReceiptsTable rows={showRows} />
                </div>
              )}
            </>
          ) : (
            <ReceiptsTable rows={receipts!} />
          )}
        </div>
      )}

      {/* 7. Divider */}
      <div className="border-t border-black/[0.08] my-2" />

      {/* 8. Action button — LOCKED COPY, BOTTOM-RIGHT */}
      <div className="flex justify-end mt-1">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpenModal();
          }}
          className="anim-tap token-13 text-ds-accent hover:underline"
        >
          Open full view →
        </button>
      </div>
    </div>,
    document.body
  );
};

// ---- Main export -----------------------------------------------------------

interface ReceiptsPopoverProps {
  repId: string;
  metric: LeaderboardMetric;
  label: string;
  formula: string;
  computation: string;
  receipts: Receipt[] | undefined;
  onOpenModal: () => void;
  children: React.ReactNode;
}

export default function ReceiptsPopover(props: ReceiptsPopoverProps): React.JSX.Element {
  const { repId, metric, label, formula, computation, receipts, onOpenModal, children } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const openTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);
  const closeTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);
  // Touch devices synthesize mouseenter/click on tap (iOS/Android hover emulation) — gating
  // hover handlers on a real hover-capable pointer avoids a tap opening then immediately
  // re-closing itself via the synthetic mouseenter that precedes click. Same pattern as
  // components/stoplights/HoverSelect.tsx.
  const hoverCapable = useRef(true);

  useEffect(() => {
    hoverCapable.current = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  }, []);

  const tooltipKey = `receipts-${repId}-${metric}`;

  const open = useCallback(() => {
    clearTimeout(closeTimeout.current);
    if (wrapperRef.current) {
      setAnchorRect(wrapperRef.current.getBoundingClientRect());
    }
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const scheduleOpen = useCallback(() => {
    clearTimeout(closeTimeout.current);
    openTimeout.current = setTimeout(open, 200);
  }, [open]);

  const scheduleClose = useCallback(() => {
    clearTimeout(openTimeout.current);
    closeTimeout.current = setTimeout(close, 250);
  }, [close]);

  // Cancel pending timers on unmount
  useEffect(() => {
    return () => {
      clearTimeout(openTimeout.current);
      clearTimeout(closeTimeout.current);
    };
  }, []);

  // Click outside → close
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapperRef.current && wrapperRef.current.contains(target)) return;
      const tooltipEl = document.querySelector(`[data-tooltip-key="${tooltipKey}"]`);
      if (tooltipEl && tooltipEl.contains(target)) return;
      close();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen, close, tooltipKey]);

  // Scroll outside tooltip → close
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: Event) => {
      const tooltipEl = document.querySelector(`[data-tooltip-key="${tooltipKey}"]`);
      if (tooltipEl && tooltipEl.contains(e.target as Node)) return;
      close();
    };
    window.addEventListener("scroll", handler, true);
    return () => window.removeEventListener("scroll", handler, true);
  }, [isOpen, close, tooltipKey]);

  const handleTooltipMouseEnter = useCallback(() => {
    clearTimeout(closeTimeout.current);
    clearTimeout(openTimeout.current);
  }, []);

  const handleTooltipMouseLeave = useCallback(() => {
    scheduleClose();
  }, [scheduleClose]);

  // Touch has no hover — tap toggles the popover directly so the receipts
  // drill-down isn't 100% hover-locked on mobile/tablet. Desktop hover flow
  // only fires on a genuine hover-capable pointer (below), so a tap can't
  // also trigger a synthetic mouseenter that fights this toggle.
  const handleTap = useCallback(() => {
    if (hoverCapable.current) return; // desktop: hover already owns open/close
    clearTimeout(openTimeout.current);
    clearTimeout(closeTimeout.current);
    if (isOpen) {
      close();
    } else {
      open();
    }
  }, [isOpen, open, close]);

  return (
    <>
      {/* Cell wrapper — hover triggers popover (desktop); tap toggles it (touch). Click is not INERT anymore. */}
      <div
        ref={wrapperRef}
        onMouseEnter={() => hoverCapable.current && scheduleOpen()}
        onMouseLeave={() => hoverCapable.current && scheduleClose()}
        onClick={handleTap}
      >
        {children}
      </div>

      {isOpen && anchorRect && (
        <PopoverPanel
          label={label}
          formula={formula}
          computation={computation}
          receipts={receipts}
          metric={metric}
          repId={repId}
          anchorRect={anchorRect}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
          onOpenModal={onOpenModal}
        />
      )}
    </>
  );
}
